import React, { useEffect, useState } from "react"

import { useQuery } from "react-query"
import { useDispatch, useSelector } from "react-redux"
import { Navigate, Route, Routes, useLocation } from "react-router-dom"

import Hidden from "@mui/material/Hidden"
import type { AxiosError, AxiosResponse } from "axios"

import { ContextProvider } from "../../features/ModelConfigurations/Context"
import { setCurrentModel } from "../../features/Models"
import { Registry } from "../../features/Registry/Registry"
import { setKonanPermissions, setSidPermissions } from "../../features/Roles/PermissionsSlice"
import { setComputedFeatures, setConstantFeatures, setCurrentProject, setSchemaFeatures } from "../../features/projects"
import { ProjectCreationFlow } from "../../features/projects/ProjectCreationFlow"
import { PageFooter } from "../../layouts/PageFooter"
import { PageHeader } from "../../layouts/PageHeader"
import { ModelConfigurations } from "../../pages/ModelConfigurations"
import { ModelDetails } from "../../pages/ModelDetails"
import { ProjectDetails } from "../../pages/ProjectDetails"
import { Projects } from "../../pages/Projects"
import { Settings } from "../../pages/Settings"
import { KonanAPI } from "../../services/KonanAPI"
import { SidAPI } from "../../services/SidAPI"
import type { RootState } from "../../store/ReduxStore"
import { DashboardAppBar } from "./components/DashboardAppBar"
import { DashboardDrawer } from "./components/DashboardDrawer"

import styles from "./Dashboard.module.scss"

export function Dashboard(): React.ReactElement {
  const currentProject = useSelector((state: RootState) => state.project.currentProject)

  const location = useLocation()
  const dispatch = useDispatch()

  const [mobileOpen, setMobileOpen] = useState(false)

  // Fetching organization roles
  useQuery<AxiosResponse, AxiosError>(["sid-permissions"], SidAPI.fetchSIDPermissions, {
    refetchOnMount: true,
    onSuccess: (response) => {
      dispatch(setSidPermissions(response))
    },
  })

  // Fetching organization roles
  useQuery<AxiosResponse, AxiosError>(["konan-permissions"], () => KonanAPI.fetchKonanPermissions(), {
    refetchOnMount: false,
    onSuccess: (response) => {
      dispatch(setKonanPermissions(response))
    },
  })

  const handleDrawerToggle = (): void => {
    setMobileOpen(!mobileOpen)
  }

  useEffect(() => {
    if (!location.pathname.includes("/projects/")) {
      //reset current project's global state in projects page
      dispatch(setCurrentProject(undefined))
      //reset current model's global state in projects page
      dispatch(setCurrentModel(undefined))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname])

  // empty project features (schema, computed and constant) if there's no currentProject
  // or it's not a workflow project
  useEffect(() => {
    if (!currentProject?.uuid) {
      dispatch(setSchemaFeatures([]))
      dispatch(setComputedFeatures([]))
      dispatch(setConstantFeatures([]))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentProject?.uuid])

  return (
    <div className={styles.root}>
      {/* Show AppBar on small screen sizes only, so that the user can open/close the drawer */}
      <Hidden smUp>
        <DashboardAppBar handleDrawerToggle={handleDrawerToggle} />
      </Hidden>
      <DashboardDrawer
        handleDrawerToggle={handleDrawerToggle}
        mobileOpen={mobileOpen}
        orgName={useSelector((state: RootState) => state.auth.organizationName) ?? ""}
      />

      <main className={styles.contentContainer} id="main">
        {location?.pathname && location?.pathname !== "/projects/new-model" && <PageHeader />}
        <Routes>
          <Route path={"*"} element={<Navigate to={"projects"} replace />} />
          <Route key="Settings" path={`settings`} element={<Settings />} />
          <Route key="registry" path="kcr" element={<Registry />} />
          <Route key="projects" path={`projects`} element={<Projects />} />
          <Route key="new-project" path={`projects/new-model`} element={<ProjectCreationFlow />} />
          <Route key="project-details" path={`projects/:id/*`} element={<ProjectDetails />} />
          <Route key="model-details" path={`projects/:id/models/*`} element={<ModelDetails />} />
          <Route
            key="model-configurations"
            path={`projects/:id/models/configurations`}
            element={
              <ContextProvider>
                <ModelConfigurations />
              </ContextProvider>
            }
          />
        </Routes>
        <PageFooter />
      </main>
    </div>
  )
}
